import {
  "ui-gesture.less" as lessLabel,
  "ui-gesture.more" as moreLabel,
  "ui-gesture.previous" as previousLabel,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { Minus, Plus } from "lucide-react";
import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useState,
} from "react";
import { cx } from "../../lib/cx";
import { impact } from "../../native";
import {
  applyKeyMove,
  keyMove,
  RULER_PX,
  rulerOffset,
  STEP_PX,
  stepBy,
  valueAtDrag,
} from "./scrubber-math";

export interface ScrubberProps {
  className?: string;
  /** How the number is written; defaults to the active locale's. */
  format?: (value: number) => string;
  label: string;
  max: number;
  /** Default 0. */
  min?: number;
  onChange: (next: number) => void;
  /** What this value was last time, or `null` when there is none. */
  previous: number | null;
  /** Said instead of the "previous" line when `previous` is `null`. */
  previousEmpty: string;
  /**
   * `spinbutton` (the workout's load and reps) or `slider` (the profile's
   * big numbers). The gesture, the keys and the look are the same.
   */
  role?: "spinbutton" | "slider";
  step: number;
  /** Shown after the number and spoken with it; `""` for a bare count. */
  unit: string;
  value: number;
}

function localeFormat(value: number): string {
  return new Intl.NumberFormat(getLocale(), {
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * A big number you drag sideways. Finger RIGHT is more, a step every
 * `STEP_PX` (22) pixels, a light haptic per step. The ruler under it moves
 * one tick per step — with the finger while dragging, with the value
 * otherwise — so −/+, the keys and the drag read as the same dial.
 *
 * Merged from A2's `training/scrubber.tsx:16-171` (the look, `spinbutton`)
 * and `profile/big-number.tsx:14-191` (`slider`, `min`, Home/End). Each fix
 * below was found on a phone:
 *
 * - `touch-none select-none` on the drag surface. Without it the WebView
 *   takes the horizontal move as a scroll and fires `pointercancel` partway
 *   through the drag.
 * - `setPointerCapture` on pointerdown, so a finger that leaves the surface
 *   keeps driving it; the drag resets on `pointerup`, `pointercancel` AND
 *   `lostpointercapture`, whichever comes.
 * - The keys call `preventDefault` AND `stopPropagation`. The propagation
 *   stop was for the retired deck, which listened for ← → on `window`;
 *   nothing that hosts a scrubber listens there now (the wrapped story's
 *   window-level ↑ ↓ never hosts one), so it is defensive only — it keeps a
 *   future window-level listener from also acting on a key the dial used.
 * - The haptic is `impact("light")` from `src/native`, not
 *   `navigator.vibrate`, which does nothing on iOS and is patchy in the
 *   Android WebView.
 */
export function Scrubber({
  label,
  value,
  unit,
  step,
  min: minProp,
  max,
  onChange,
  previous,
  previousEmpty,
  role,
  format: formatProp,
  className,
}: ScrubberProps) {
  // Defaults here, not in the destructuring: the React Compiler 1.0 skips a
  // component whose parameter pattern has a default ("AssignmentPattern").
  const min = minProp ?? 0;
  const format = formatProp ?? localeFormat;
  // Where the drag began (the pointer's x and the value it started on) and
  // how far the finger has gone since. `null` while nothing is dragging.
  const [drag, setDrag] = useState<{
    x: number;
    start: number;
    dx: number;
  } | null>(null);

  const bump = useCallback(
    (steps: number) => {
      const next = stepBy(value, steps, { step, min, max });
      if (next !== value) {
        onChange(next);
      }
    },
    [value, step, min, max, onChange]
  );
  const onLess = useCallback(() => bump(-1), [bump]);
  const onMore = useCallback(() => bump(1), [bump]);

  const endDrag = useCallback(() => setDrag(null), []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const move = keyMove(event.key);
      if (move === null) {
        return;
      }
      // Both: preventDefault keeps PageUp/Down and the arrows from scrolling,
      // stopPropagation is defensive: no window-level arrow listener is
      // mounted beside a scrubber today (see the doc comment above).
      event.preventDefault();
      event.stopPropagation();
      const next = applyKeyMove(value, move, { step, min, max });
      if (next !== value) {
        onChange(next);
      }
    },
    [value, step, min, max, onChange]
  );

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button > 0) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({ x: event.clientX, start: value, dx: 0 });
    },
    [value]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (drag === null) {
        return;
      }
      const moved = event.clientX - drag.x;
      setDrag({ ...drag, dx: moved });
      const next = valueAtDrag(drag.start, moved, { step, min, max });
      if (next !== value) {
        onChange(next);
        impact("light");
      }
    },
    [drag, value, step, min, max, onChange]
  );

  const offset = rulerOffset(value, step, drag, STEP_PX);
  const delta = previous === null ? 0 : value - previous;
  const spoken = unit ? `${format(value)} ${unit}` : format(value);
  const named = label.toLocaleLowerCase(getLocale());
  const button =
    "a-press grid h-12 place-items-center rounded-lg bg-surface text-ink shadow-card disabled:opacity-40";

  return (
    <div className={cx("rounded-2xl bg-canvas p-1.5", className)}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the role is `spinbutton` or `slider`, set from a prop Biome cannot read. */}
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: as above — both roles are interactive widgets. */}
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-value* are the props of both roles. */}
      <div
        aria-label={label}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={value}
        aria-valuetext={spoken}
        className="t-grab relative flex h-[126px] touch-none select-none flex-col items-center overflow-hidden rounded-lg pt-2.5"
        data-dragging={drag === null ? undefined : ""}
        onKeyDown={onKeyDown}
        onLostPointerCapture={endDrag}
        onPointerCancel={endDrag}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        role={role ?? "spinbutton"}
        tabIndex={0}
      >
        <span className="font-medium text-ink-2 text-sm leading-none">
          {label}
        </span>
        <span className="mt-1.5 flex items-baseline gap-1">
          <span
            className="a-display text-[46px] text-ink tabular-nums leading-none"
            data-value=""
          >
            {format(value)}
          </span>
          {unit ? (
            <span className="font-semibold text-base text-ink-3">{unit}</span>
          ) : null}
        </span>
        <span className="mt-1.5 flex h-4 items-center gap-1.5 text-xs tabular-nums leading-none">
          {previous === null ? (
            <span className="text-ink-3">{previousEmpty}</span>
          ) : (
            <>
              <span className="text-ink-3">
                {previousLabel()}{" "}
                <span className="font-semibold text-ink-2">
                  {format(previous)}
                </span>
              </span>
              {delta === 0 ? null : (
                <span
                  className={cx(
                    "font-semibold",
                    delta > 0 ? "text-success-ink" : "text-ink-3"
                  )}
                >
                  {delta > 0 ? "+" : "−"}
                  {format(Math.abs(delta))}
                </span>
              )}
            </>
          )}
        </span>
        <span
          aria-hidden="true"
          className="absolute bottom-2.5 left-1/2 h-3 -translate-x-1/2"
          style={{ width: RULER_PX }}
        >
          <span
            className={cx(
              "t-ruler absolute inset-0",
              drag === null && "t-ruler-settle"
            )}
            data-ruler=""
            style={
              {
                "--step-px": `${STEP_PX}px`,
                backgroundPositionX: `${offset}px, ${offset}px`,
              } as CSSProperties
            }
          />
          <span
            className="absolute top-0 -bottom-0.5 w-[2px] rounded-full bg-accent"
            style={{ left: RULER_PX / 2 }}
          />
        </span>
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        <button
          aria-label={lessLabel({ label: named })}
          className={button}
          disabled={value <= min}
          onClick={onLess}
          type="button"
        >
          <Minus className="size-5" strokeWidth={2.5} />
        </button>
        <button
          aria-label={moreLabel({ label: named })}
          className={button}
          disabled={value >= max}
          onClick={onMore}
          type="button"
        >
          <Plus className="size-5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
